import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { signOut } from "@aws-amplify/auth";
import { clearAllTokens } from '../utils/tokenUtils';
import axios from 'axios';
import { logActivity } from '../utils/activityLogApi';


const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children, user, setUser, authConfig }) => {

  const [profile, setProfile] = useState(null);

  // ── Fetch profile on login ──────────────────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      const isLoggingOut = sessionStorage.getItem("loggingOut");
      if (isLoggingOut) return;
      if (!user?.["cognito:username"]) return;

      try {
        const region = sessionStorage.getItem("userRegion") || "AP";

        const res = await axios.get(
          `https://huh5datcfj.execute-api.ap-southeast-2.amazonaws.com/prod/api/users/${user["cognito:username"]}`,
          { headers: { Region: region } }
        );

        // 1️⃣ Set profile
        setProfile({
          ...res.data,
          cognitogroups: user?.["cognito:groups"] || [],
          orgId: res.data?.orgId || user?.["custom:orgId"] || '',
        });

        // 2️⃣ Log login event
        logActivity({
          userData:    user,
          orgId:       res.data?.orgId || '',
          region:      region,
          module:      'Settings',
          category:    'Authentication',
          action:      'User Login',
          severity:    'success',
          description: `${user?.email || user?.['cognito:username']} logged in`,
        });

        

        console.log("PROFILE FROM BACKEND:", res.data);

      } catch (err) {
        console.error("❌ Failed to fetch profile:", err);
      }
    };

    fetchProfile();
  }, [user]);

  // ── Sign out ────────────────────────────────────────────────────
  const handleSignOut = useCallback(() => {

    // 1️⃣ Log logout FIRST (before user data is cleared)
    logActivity({
      userData:    user,
      orgId:       profile?.orgId || '',
      region:      sessionStorage.getItem('userRegion') || 'AP',
      module:      'Settings',
      category:    'Authentication',
      action:      'User Logout',
      severity:    'info',
      description: `${user?.email || user?.['cognito:username']} signed out`,
    });

    // 2️⃣ Clear all tokens and state
    clearAllTokens();
    setUser(null);
    setProfile(null);
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("loggingOut");

    // 3️⃣ Build logout URL
    const domain         = authConfig?.oauth?.domain;
    const clientId       = authConfig?.aws_user_pools_web_client_id || "689baoimsmi285997eflt8hdqa";
    const appRedirectUri = authConfig?.oauth?.redirectSignOut || 'https://demo.securecloud.tech/';
    const logoutUrl      = `${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(appRedirectUri)}`;

    console.log("🌍 Logging out → Redirecting to:", logoutUrl);

    // 4️⃣ Cognito sign out
    signOut()
      .then(() => { window.location.href = logoutUrl; })
      .catch((error) => {
        console.error("Sign out error:", error);
        window.location.href = logoutUrl;
      });

  }, [authConfig, setUser, setProfile, user, profile]);

  const value = {
    user,
    profile,
    signOut:         handleSignOut,
    isAuthenticated: !!user,
    authConfig,
    isAdmin:         (profile?.cognitogroups || user?.["cognito:groups"] || []).includes('Admin'),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};